import { BoughtCouponsPackBase } from "../json-schemas/base/bought_coupons_pack"
import { CouponPackBase } from "../json-schemas/base/coupon_pack"
import { CustomerCouponsPackNewResponse } from "../json-schemas/serializers/customer-coupons-pack-new-response"
import { httpGet, httpGetRoute, httpPost } from "../utils/http.service"
import { I18nService } from "../utils/i18n.service"
import { couponsPackI18nValidityDurationInMonths } from "./coupons-packs.service"
import { moment } from "../utils/moment.service"
import { CustomerBoughtCouponsPacksIndexProps } from "../json-schemas/serializers/customer-bought-coupons-packs-index-props"
import { BackofficeBoughtCouponsPacksIndexResponse } from "../json-schemas/serializers/backoffice-bought-coupons-packs-index-response"
import i18nScopedData from './bought-coupons-packs.service.yml'

const i18nScoped = new I18nService(i18nScopedData)

export interface BackofficeRansackQuery {
  'q[id_not_in][]'?: number[]
  'q[coupons_pack_id_eq]'?: number
  'q[paid_at_not_null]'?: boolean
}

export function boughtCouponsPackList (query: BackofficeRansackQuery = {}): Promise<BackofficeBoughtCouponsPacksIndexResponse> {
  return httpGet(`/${httpGetRoute()}/bought_coupons_packs`, query)
}

export function boughtCouponsPackNew (bought_coupons_pack_id?: BoughtCouponsPackBase['id']): Promise<CustomerCouponsPackNewResponse> {
  return httpGet(`/${httpGetRoute()}/bought_coupons_packs/new`, { bought_coupons_pack_id })
}

export function boughtCouponsPackCreate (coupons_pack_id: CouponPackBase['id']): Promise<{ redirect_url: string }> {
  return httpPost(`/${httpGetRoute()}/bought_coupons_packs/`, { bought_coupons_pack: { coupons_pack_id } })
}

export function boughtCouponsPackI18nValidUntil (createdAt: string, validityDurationInMonths: number | null) {
  const formattedValidityDuration = couponsPackI18nValidityDurationInMonths(validityDurationInMonths)
  if (!validityDurationInMonths) return formattedValidityDuration
  return i18nScoped.t('valid_until', { validity_duration: formattedValidityDuration, start: moment(createdAt).format('ll') })
}

export function boughtCouponsPackConsumptionInsights (
  quantityLeftForBoughtCouponsPacks: CustomerBoughtCouponsPacksIndexProps['customerCouponsPacksSimulationResult']['quantityLeftForBoughtCouponsPacks'],
  boughtCouponsPack: Pick<BoughtCouponsPackBase, 'id' | 'get_quantity_left'> & { coupons_pack: Pick<CouponPackBase, 'quantity'> }
) {
  const foundQuantityLeft = quantityLeftForBoughtCouponsPacks.find(_ => _.bought_coupons_pack.id === boughtCouponsPack.id)?.quantity_left

  /**
   * `quantityLeftForBoughtCouponsPacks.quantity_left` can be negative because customer#simulate_coupons_consumption
   * tries to consume bought coupons packs before giving up if there is no enough quantity.
   * 
   * If `foundQuantityLeft` is undefined, this bought coupons packs is not yet used and therefore 
   * it is full of `coupons_pack.quantity` hours.
   */
  const simulatedIncomingQuantityLeft = foundQuantityLeft ?? boughtCouponsPack.get_quantity_left

  /**
   * The duration that is planned, but not yet consumed.
   * This includes both the duration for the incomming week and the duration for past event that did not yet go through the invoicing task (not having event.coupons yet).
   */
  const simulatedIncomingQuantityPlanned = boughtCouponsPack.get_quantity_left - simulatedIncomingQuantityLeft
  
  /**
   * The duration of events that did already go through the invoicing task (events.coupons generated).
   * When used, we should consider computing volume of events not yet processed (events.coupons being nil).
   */
  const quantityUsed = boughtCouponsPack.coupons_pack.quantity - boughtCouponsPack.get_quantity_left
  const simulatedQuantityLeft = boughtCouponsPack.coupons_pack.quantity - simulatedIncomingQuantityPlanned - quantityUsed

  return { simulatedIncomingQuantityPlanned, quantityUsed, simulatedQuantityLeft }
}
